import React, { useEffect, useState } from "react";
import {
  calcularSubtotalPreview,
  calcularLineaPreview,
} from "../utils/carritoCalculos";

const CANTIDAD_MAXIMA = 99;

function ControlCantidad({ cantidad, nombre, onCambiar }) {
  const valor = Math.max(1, Number(cantidad) || 1);
  const [texto, setTexto] = useState(String(valor));

  useEffect(() => {
    setTexto(String(valor));
  }, [valor]);

  function aplicar(siguiente) {
    const n = Math.floor(Number(siguiente));
    if (!Number.isFinite(n)) {
      return;
    }
    const limitada = Math.min(CANTIDAD_MAXIMA, Math.max(1, n));
    if (limitada !== valor) {
      onCambiar?.(limitada);
    }
    setTexto(String(limitada));
  }

  return (
    <div className="carrito-cantidad" role="group" aria-label={`Cantidad de ${nombre}`}>
      <button
        type="button"
        className="carrito-cantidad__btn"
        aria-label={`Quitar uno de ${nombre}`}
        disabled={valor <= 1}
        onClick={() => aplicar(valor - 1)}
      >
        −
      </button>
      <input
        className="carrito-cantidad__input"
        inputMode="numeric"
        aria-label={`Cantidad de ${nombre}`}
        value={texto}
        onChange={(e) => {
          const limpio = e.target.value.replace(/\D/g, "");
          if (limpio === "") {
            setTexto("");
            return;
          }
          const n = Math.min(CANTIDAD_MAXIMA, Number(limpio));
          if (n < 1) {
            setTexto(limpio);
            return;
          }
          setTexto(String(n));
          onCambiar?.(n);
        }}
        onBlur={() => {
          if (!texto || Number(texto) < 1) {
            setTexto(String(valor));
          }
        }}
      />
      <button
        type="button"
        className="carrito-cantidad__btn"
        aria-label={`Sumar uno de ${nombre}`}
        disabled={valor >= CANTIDAD_MAXIMA}
        onClick={() => aplicar(valor + 1)}
      >
        +
      </button>
    </div>
  );
}

export function ResumenItemsPedido({ items = [], incrustado = false }) {
  if (!items.length) {
    return null;
  }

  return (
    <div
      className={incrustado ? "mb-3" : "mb-3 rounded-3 p-3"}
      style={incrustado ? undefined : { backgroundColor: "#f8f9fa" }}
    >
      <div className="fw-semibold mb-2">Tu pedido</div>
      {items.map((item, i) => (
        <div key={i} className="mb-2" style={{ fontSize: 15 }}>
          <div className="fw-semibold">
            {item.nombre}{" "}
            <span className="text-secondary fw-normal">
              ×{item.cantidad || 1}
            </span>
          </div>
          {item.extras && item.extras.length > 0 ? (
            <div className="text-muted" style={{ fontSize: 14 }}>
              Extras: {item.extras.map((extra) => extra.nombre).join(", ")}
            </div>
          ) : null}
          {item.comentario ? (
            <div className="text-muted fst-italic" style={{ fontSize: 14 }}>
              {item.comentario}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default function CarritoPanel({
  productos,
  onEliminar,
  onEditar,
  onCambiarCantidad,
  onVaciar,
  modoEntrega,
  costoDelivery = 0,
  horariosDisponibles = [],
  horarioSeleccionado = "",
  onHorarioChange,
  onFinalizar,
  enviandoPedido = false,
  pedidosCerrados = false,
  mensajeCierre = null,
}) {
  const subtotalPreview = calcularSubtotalPreview(productos);
  const totalConDelivery =
    subtotalPreview +
    (modoEntrega === "domicilio" ? Number(costoDelivery || 0) : 0);

  return (
    <div
      className="bg-white rounded-4 shadow px-4 pb-4 pt-3"
      style={{
        maxWidth: 440,
        minHeight: 340,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        border: "1px solid #ececec",
      }}
    >
      <h2 className="fw-bold mb-3" style={{ fontSize: 25 }}>
        Mi pedido
      </h2>
      <hr />
      {productos.length === 0 ? (
        <div className="text-center text-muted py-4" style={{ fontSize: 17 }}>
          El carrito está vacío
        </div>
      ) : (
        <>
          {productos.map((producto, i) => (
            <div
              key={i}
              className="mb-4 pb-2"
              style={{ borderBottom: "1.8px solid #e5e5e5" }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="fw-bold" style={{ fontSize: 21 }}>
                    {producto.nombre}
                  </div>
                  <div
                    className="d-flex align-items-center flex-wrap"
                    style={{ gap: 10, marginTop: 6 }}
                  >
                    <ControlCantidad
                      cantidad={producto.cantidad || 1}
                      nombre={producto.nombre}
                      onCambiar={(cantidad) => onCambiarCantidad?.(i, cantidad)}
                    />
                    <span
                      className="fw-bold"
                      style={{ color: "#e53935", fontSize: 18 }}
                    >
                      ${calcularLineaPreview(producto).toLocaleString("es-CL")}
                    </span>
                  </div>
                  {producto.extras && producto.extras.length > 0 && (
                    <div className="text-muted" style={{ fontSize: 15 }}>
                      Extras: {producto.extras.map((extra) => extra.nombre).join(", ")}
                    </div>
                  )}
                  {producto.comentario && (
                    <div
                      className="text-muted fst-italic"
                      style={{ fontSize: 15 }}
                    >
                      {producto.comentario}
                    </div>
                  )}
                </div>
                <div className="d-flex flex-column align-items-end gap-2">
                  <button
                    className="btn btn-link p-0 text-primary"
                    style={{
                      fontWeight: 600,
                      fontSize: 17,
                      textDecoration: "none",
                    }}
                    onClick={() => onEditar?.(i)}
                  >
                    {producto.armable || producto.con_tamanos
                      ? producto.armable && producto.con_tamanos
                        ? "Editar"
                        : producto.con_tamanos
                          ? "Editar tamaño"
                          : "Editar armado"
                      : "Editar"}
                  </button>
                  <button
                    className="btn p-0"
                    onClick={() => onEliminar(i)}
                    style={{
                      background: "none",
                      border: "none",
                    }}
                  >
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="#9e9e9e"
                    >
                      <rect x="5" y="7" width="14" height="12" rx="2.5" />
                      <rect
                        x="9"
                        y="11"
                        width="1.5"
                        height="5.5"
                        rx="0.75"
                        fill="#fff"
                      />
                      <rect
                        x="13.5"
                        y="11"
                        width="1.5"
                        height="5.5"
                        rx="0.75"
                        fill="#fff"
                      />
                      <rect x="8" y="3" width="8" height="3" rx="1.5" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}

          <div
            className="pt-1 mt-2"
            style={{ borderTop: "2px dashed #e5e5e5" }}
          >
            <div
              className="fw-bold mb-1"
              style={{ fontSize: 20, marginTop: 8 }}
            >
              Resumen
            </div>
            <div
              className="d-flex justify-content-between align-items-center mb-2"
              style={{ fontSize: 18 }}
            >
              <span className="fw-normal text-secondary">Subtotal</span>
              <span className="fw-bold">
                ${subtotalPreview.toLocaleString("es-CL")}
              </span>
            </div>
            {modoEntrega === "domicilio" && (
              <div
                className="d-flex justify-content-between align-items-center mb-2"
                style={{ fontSize: 18 }}
              >
                <span className="fw-normal text-secondary">Delivery</span>
                <span className="fw-bold">
                  ${Number(costoDelivery || 0).toLocaleString("es-CL")}
                </span>
              </div>
            )}
            <div
              className="fw-bold d-flex align-items-center justify-content-between"
              style={{ fontSize: 22, marginTop: 10 }}
            >
              <span>Total</span>
              <span style={{ color: "#e53935" }}>
                ${totalConDelivery.toLocaleString("es-CL")}
              </span>
            </div>
          </div>

          <div className="mt-3">
            <label className="form-label fw-semibold mb-1">Horario del pedido</label>
            {pedidosCerrados ? (
              <div className="text-muted" style={{ fontSize: 15 }}>
                {mensajeCierre || "Estamos cerrados. Podrás pedir el próximo día de atención."}
              </div>
            ) : (
              <select
                className="form-select"
                value={horarioSeleccionado}
                onChange={(e) => onHorarioChange?.(e.target.value)}
              >
                <option value="">Selecciona un horario</option>
                {horariosDisponibles.map((hora) => (
                  <option key={hora} value={hora}>
                    {hora}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="d-flex justify-content-between gap-2 mt-4">
            <button
              className="btn btn-outline-dark rounded-pill px-3"
              onClick={onVaciar}
              disabled={productos.length === 0}
              style={{ fontWeight: 600, fontSize: 16 }}
            >
              Vaciar
            </button>
            <button
              className="btn btn-dark rounded-pill px-3"
              onClick={onFinalizar}
              disabled={
                productos.length === 0 || enviandoPedido || pedidosCerrados
              }
              style={{ fontWeight: 600, fontSize: 16 }}
            >
              {enviandoPedido
                ? "Enviando…"
                : pedidosCerrados
                  ? "Cerrado"
                  : "Finalizar"}
            </button>
          </div>
        </>
      )}
      <style>
        {`
          .carrito-cantidad {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .carrito-cantidad__btn {
            width: 36px;
            height: 36px;
            border-radius: 999px;
            border: 1.5px solid #212529;
            background: #fff;
            color: #212529;
            font-size: 20px;
            font-weight: 700;
            line-height: 1;
            padding: 0;
          }
          .carrito-cantidad__btn:disabled {
            opacity: 0.35;
          }
          .carrito-cantidad__input {
            width: 48px;
            height: 36px;
            text-align: center;
            font-weight: 700;
            font-size: 17px;
            border: 1.5px solid #e5e5e5;
            border-radius: 10px;
            padding: 0;
          }
          .carrito-cantidad__input:focus {
            outline: none;
            border-color: #212529;
          }
          @media (max-width: 767.98px) {
            div.bg-white.rounded-4.shadow {
              margin-left: auto !important;
              margin-right: auto !important;
              max-width: 100% !important;
              min-height: auto !important;
              padding-left: 1rem !important;
              padding-right: 1rem !important;
            }
          }
        `}
      </style>
    </div>
  );
}
